import { useEffect, useRef, useState } from "react";
import { useWorldT } from "../i18n.js";
import { TIME_PRESETS, formatHours, hoursForSetting, presetOf, sunAt } from "../look/time.js";
import type { TimePreset } from "../look/time.js";
import { previewTimeSetting, setTimeSetting, useTimeSetting } from "../look/timeStore.js";

const PRESETS: readonly TimePreset[] = ["day", "dusk", "night"];

/** The time-of-day control of the HUD: Auto (clock), Day, Dusk, Night and an hour slider. */
export function TimeControl() {
  const t = useWorldT();
  const setting = useTimeSetting();
  const [open, setOpen] = useState(false);
  const hours = hoursForSetting(setting);
  const preset = presetOf(setting);
  const sun = sunAt(hours);
  const commit = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(commit.current), []);
  const mode = setting === "auto" ? "auto" : (preset ?? "custom");
  return (
    <div
      className="world-time"
      data-testid="world-time"
      data-setting={String(setting)}
      data-hours={hours.toFixed(2)}
      data-mode={mode}
    >
      <button
        type="button"
        className="world-time-toggle"
        data-testid="world-time-toggle"
        aria-expanded={open}
        aria-label={t("time.label")}
        title={t("time.label")}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">{sun.night > 0.5 ? "☾" : "☀"}</span>
        <span className="world-time-clock">{formatHours(hours)}</span>
      </button>
      {open ? (
        <div className="world-time-panel" role="group" aria-label={t("time.label")}>
          <div className="world-time-presets">
            <button
              type="button"
              aria-pressed={setting === "auto"}
              data-testid="time-auto"
              onClick={() => setTimeSetting("auto")}
            >
              {t("time.auto")}
            </button>
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={preset === p}
                data-testid={`time-${p}`}
                onClick={() => setTimeSetting(TIME_PRESETS[p])}
              >
                {t(`time.${p}`)}
              </button>
            ))}
          </div>
          <label className="world-time-slider">
            <span>{t("time.hour")}</span>
            <input
              type="range"
              min={0}
              max={24}
              step={0.25}
              value={hours}
              data-testid="time-slider"
              aria-valuetext={formatHours(hours)}
              onChange={(e) => {
                const v = Number(e.target.value);
                previewTimeSetting(v);
                clearTimeout(commit.current);
                commit.current = setTimeout(() => setTimeSetting(v), 350);
              }}
            />
            <output>{formatHours(hours)}</output>
          </label>
        </div>
      ) : null}
    </div>
  );
}
